// tokens.js
// =============================================================================
// EFabric design tokens — the SINGLE SOURCE OF TRUTH for the signed-in app.
//
// Locked high-level design decisions (2026 refresh):
//   • ONE green accent  ........ #3F9A82 (the established brand teal-green)
//   • Neutral slate ramp ....... canvas / surface / hairline / ink text
//   • Restrained elevation ..... soft neutral shadows, no colored glows
//   • Two-step radius scale .... 8px controls, 12px cards
//   • One type scale ........... xs 12 → xxl 24 (no ad-hoc rem values)
//   • Semantic colors .......... reserved for status only
//   • Category colors .......... fixed mapping for CPU / RAM / GPU / pods
//
// Page/content components should import from here instead of hardcoding
// hex values or one-off font sizes. appTheme.js consumes these tokens.
// =============================================================================

// --- Accent (locked: the established #3F9A82) ---
export const ACCENT = "#3F9A82";
export const ACCENT_DARK = "#2F7A66";
export const ACCENT_TINT = "rgba(63,154,130,0.10)"; // subtle fill (hover/selected)
export const ACCENT_TINT_STRONG = "rgba(63,154,130,0.16)";

// --- Neutral / slate ramp ---
export const CANVAS = "#F8FAFC"; // app content canvas
export const SURFACE = "#FFFFFF"; // cards / panels
export const SURFACE_MUTED = "#F1F5F9"; // subtle fills (table head, etc.)
export const HAIRLINE = "#E2E8F0"; // borders & dividers (slate-200)
export const HAIRLINE_STRONG = "#CBD5E1"; // emphasized border (slate-300)

export const INK = "#0F172A"; // text primary (slate-900)
export const INK_SECONDARY = "#475569"; // text secondary (slate-600)
export const INK_MUTED = "#94A3B8"; // muted / placeholders (slate-400)
export const ICON = "#64748B"; // default icon color (slate-500)

export const HOVER = "rgba(15,23,42,0.04)"; // neutral hover wash

// --- Elevation (restrained, neutral — no heavy or colored shadows) ---
export const SHADOW_SM =
  "0 1px 2px rgba(16,24,40,0.04), 0 1px 3px rgba(16,24,40,0.06)";
export const SHADOW_MD =
  "0 1px 2px rgba(16,24,40,0.04), 0 4px 12px rgba(16,24,40,0.06)";
export const SHADOW_LG = "0 4px 16px rgba(16,24,40,0.10)";

// --- Radii ---
export const RADIUS = { sm: 8, md: 12, pill: 999 };

// --- Type scale (rem) — map every text size onto one of these ---
export const FONT = {
  xs: "0.75rem", // 12 — captions, chips, table-dense
  sm: "0.8125rem", // 13 — secondary text / body2
  md: "0.875rem", // 14 — body (base)
  lg: "1rem", // 16 — emphasized body / small titles
  xl: "1.25rem", // 20 — section titles (h5)
  xxl: "1.5rem", // 24 — page titles (h4)
};

export const WEIGHT = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
};

// --- Semantic status colors (reserved for status meaning only) ---
export const STATUS = {
  success: "#2E9E6B",
  warning: "#E0922F",
  error: "#D14343",
  info: "#3B82F6",
  neutral: "#64748B",
};

// --- Category colors (resource chips & similar fixed mappings) ---
export const CATEGORY = {
  cpu: "#3B82F6", // blue
  ram: "#7C6AED", // violet (tamed)
  gpu: "#2E9E6B", // green
  pod: "#64748B", // slate
};

// Shared qualitative palette for categorical badges across the app. Keep the
// first five stable; UsersTable hashes group names against the full list.
export const BADGE_COLORS = [
  "#3B82F6", // blue
  "#8B5CF6", // violet
  "#C2650C", // amber
  "#DB2777", // rose
  "#64748B", // slate
  "#0F766E", // teal
  "#4F46E5", // indigo
  "#0891B2", // cyan
  "#16A34A", // green
  "#DC2626", // red
  "#C026D3", // fuchsia
  "#65A30D", // lime
];

// Hugging Face task families used to color model pipeline badges.
export const PIPELINE_CATEGORY = {
  multimodal: BADGE_COLORS[2],
  vision: BADGE_COLORS[0],
  nlp: BADGE_COLORS[9],
  audio: BADGE_COLORS[5],
  tabular: BADGE_COLORS[6],
  reinforcementLearning: BADGE_COLORS[10],
  other: BADGE_COLORS[4],
};

// --- Helpers ---
// Convert a #RRGGBB hex to an rgba() string at the given alpha. Used to derive
// soft tinted fills (e.g. status pills, resource chips) from solid token hexes
// so we keep a single source of truth for the base colors.
export const withAlpha = (hex, alpha) => {
  const h = String(hex).replace("#", "");
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};
